import { useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Landmark, Search, Bookmark, ExternalLink, Loader2, CheckCircle2, AlertCircle,
  HelpCircle, Trash2, ArrowDownWideNarrow, ShieldCheck, XCircle,
} from "lucide-react";

const DEMOGRAPHICS = [
  "Woman owned", "Veteran owned", "Service Disabled Veteran owned", "Minority owned",
  "Disability owned", "Native American or Tribal owned", "Rural", "None or Prefer not to say",
];
const APPLICANT_TYPES = ["Small business (for-profit)", "Nonprofit", "Individual", "Other"];
const SAVED_STATUSES = ["Saved", "Applied", "Not Eligible"];

const MATCH_STYLES = {
  "Strong Match": { cls: "bg-emerald-100 text-emerald-700", Icon: CheckCircle2 },
  "Possible Match": { cls: "bg-amber-100 text-amber-700", Icon: AlertCircle },
  "Not Enough Information": { cls: "bg-muted text-muted-foreground", Icon: HelpCircle },
  "Not Eligible": { cls: "bg-rose-100 text-rose-700", Icon: XCircle },
};
const STATUS_STYLES = {
  Saved: "bg-primary/15 text-primary",
  Applied: "bg-emerald-100 text-emerald-700",
  "Not Eligible": "bg-muted text-muted-foreground",
};

const MatchBadge = ({ level }) => {
  const m = MATCH_STYLES[level] || MATCH_STYLES["Not Enough Information"];
  const Icon = m.Icon;
  return (
    <span data-testid="match-level" className={`inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full ${m.cls}`}>
      <Icon className="h-3.5 w-3.5" /> {level}
    </span>
  );
};

const Field = ({ label, children }) => (
  <div className="space-y-1.5"><Label className="text-sm">{label}</Label>{children}</div>
);

const ClampText = ({ text, testid }) => {
  const [open, setOpen] = useState(false);
  if (!text) return null;
  const long = text.length > 280;
  const shown = open || !long ? text : text.slice(0, 280).trimEnd() + "…";
  return (
    <span data-testid={testid}>
      <span className="whitespace-pre-line">{shown}</span>
      {long && (
        <button type="button" onClick={() => setOpen((o) => !o)} data-testid={`${testid}-toggle`} className="ml-1 text-primary hover:underline font-medium">
          {open ? "View less" : "View more"}
        </button>
      )}
    </span>
  );
};

export default function GrantFinder() {
  const empty = { location: "", state: "", industry: "", years_in_business: "", annual_revenue: "", num_employees: "", owner_demographics: [], funding_needs: "", applicant_type: "" };
  const [profile, setProfile] = useState(empty);
  const [savingProfile, setSavingProfile] = useState(false);
  const [results, setResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const [searchMeta, setSearchMeta] = useState(null);
  const [saved, setSaved] = useState([]);
  const [savedSort, setSavedSort] = useState("saved");
  const [sources, setSources] = useState([]);

  const load = useCallback(async () => {
    try {
      const [p, s, src] = await Promise.all([
        api.get("/funding-profile"),
        api.get("/grants/saved"),
        api.get("/grant-sources"),
      ]);
      if (p.data && Object.keys(p.data).length) setProfile({ ...empty, ...p.data });
      setSaved(s.data || []);
      setSources(src.data?.sources || []);
    } catch (e) { /* first load */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => { load(); }, [load]);

  const toggleDemo = (d) => {
    setProfile((pr) => {
      const has = pr.owner_demographics.includes(d);
      let next = has ? pr.owner_demographics.filter((x) => x !== d) : [...pr.owner_demographics, d];
      if (d === "None or Prefer not to say" && !has) next = ["None or Prefer not to say"];
      else if (d !== "None or Prefer not to say" && !has) next = next.filter((x) => x !== "None or Prefer not to say");
      return { ...pr, owner_demographics: next };
    });
  };

  const saveProfile = async () => {
    setSavingProfile(true);
    try {
      const r = await api.put("/funding-profile", profile);
      setProfile({ ...empty, ...r.data });
      toast.success("Funding profile saved.");
    } catch (e) { toast.error("Could not save your profile."); }
    finally { setSavingProfile(false); }
  };

  const runSearch = async (all = false) => {
    setSearching(true);
    try {
      const r = await api.post(`/grants/search${all ? "?include_ineligible=true" : ""}`);
      setResults(r.data?.results || []);
      setSearchMeta({ keyword: r.data?.keyword, live: r.data?.live_sources || [], complete: r.data?.profile_complete, ineligible: r.data?.ineligible_count || 0, all });
      if (!(r.data?.results || []).length) toast.message("No currently open federal grants matched. Try broadening your industry or funding need.");
    } catch (e) { toast.error(e?.response?.data?.detail || "Grant search failed. Please try again."); }
    finally { setSearching(false); }
  };

  const savedIds = new Set(saved.map((s) => `${s.source}:${s.source_id}`));
  const saveGrant = async (opp) => {
    try {
      const r = await api.post("/grants/saved", { source: opp.source, source_id: opp.source_id, snapshot: opp, status: "Saved" });
      setSaved((prev) => [r.data, ...prev.filter((x) => x.id !== r.data.id)]);
      toast.success("Grant saved.");
    } catch (e) { toast.error("Could not save this grant."); }
  };
  const updateSaved = async (id, patch) => {
    try {
      const r = await api.patch(`/grants/saved/${id}`, patch);
      setSaved((prev) => prev.map((s) => (s.id === id ? r.data : s)));
    } catch (e) { toast.error("Could not update."); }
  };
  const removeSaved = async (id) => {
    try { await api.delete(`/grants/saved/${id}`); setSaved((prev) => prev.filter((s) => s.id !== id)); toast.success("Removed."); }
    catch (e) { toast.error("Could not remove."); }
  };

  const sortedSaved = [...saved].sort((a, b) => {
    if (savedSort === "deadline") {
      const da = a.snapshot?.deadline_sort || "9999-12-31";
      const db_ = b.snapshot?.deadline_sort || "9999-12-31";
      return da.localeCompare(db_);
    }
    return (b.saved_at || "").localeCompare(a.saved_at || "");
  });

  const OppCard = ({ opp, savedRow }) => {
    const s = savedRow ? savedRow.snapshot : opp;
    return (
      <Card className="rounded-3xl border-border shadow-soft p-6" data-testid="grant-card">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2 mb-2">
              {s.match_level && <MatchBadge level={s.match_level} />}
              <span className="inline-flex items-center gap-1 text-xs text-muted-foreground"><ShieldCheck className="h-3.5 w-3.5 text-primary" /> {s.source_name || "Grants.gov"}</span>
              {s.status && <span className="text-xs text-emerald-700">{s.status}</span>}
              {savedRow && <span className={`text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full ${STATUS_STYLES[savedRow.status]}`}>{savedRow.status}</span>}
            </div>
            <h3 className="font-heading text-xl text-foreground leading-snug">{s.name}</h3>
            {s.agency && <p className="text-sm text-muted-foreground mt-1">{s.agency}</p>}
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-x-6 gap-y-3 mt-4 text-sm">
          <div><span className="text-muted-foreground">Funding:</span> <span className="text-foreground">{s.funding_amount || <em className="text-muted-foreground">Not provided by source</em>}</span></div>
          <div><span className="text-muted-foreground">Deadline:</span> <span className="text-foreground">{s.deadline || <em className="text-muted-foreground">Not specified — verify on official page</em>}</span></div>
          <div className="sm:col-span-2"><span className="text-muted-foreground">Eligibility:</span> {s.eligibility ? <ClampText text={s.eligibility} testid="eligibility-text" /> : <em className="text-muted-foreground">Not provided by source — review official listing</em>}</div>
          {(s.applicant_types || []).length > 0 && (
            <div className="sm:col-span-2"><span className="text-muted-foreground">Eligible applicant types:</span> <span className="text-foreground">{s.applicant_types.join(", ")}</span></div>
          )}
        </div>

        {s?.match_reasons?.length > 0 && (
          <div className="mt-4 rounded-2xl bg-primary/5 p-4" data-testid="why-match">
            <p className="text-xs font-semibold text-primary uppercase tracking-wide mb-2">{s.match_level === "Not Eligible" ? "Why you're likely not eligible" : "Why this may match"}</p>
            <ul className="space-y-1 text-sm text-foreground/85">{s.match_reasons.map((r, i) => <li key={i}>• {r}</li>)}</ul>
          </div>
        )}
        {s?.verify?.length > 0 && (
          <div className="mt-3 rounded-2xl bg-amber-50 p-4" data-testid="verify-list">
            <p className="text-xs font-semibold text-amber-700 uppercase tracking-wide mb-2">Still needs verification</p>
            <ul className="space-y-1 text-sm text-amber-900/85">{s.verify.map((r, i) => <li key={i}>• {r}</li>)}</ul>
          </div>
        )}
        {s.status_note && <p className="text-xs text-amber-700 mt-3">{s.status_note}</p>}

        <div className="flex flex-wrap items-center gap-2 mt-5">
          <a href={s.official_url} target="_blank" rel="noopener noreferrer" data-testid="official-link" className="inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:underline">
            <ExternalLink className="h-4 w-4" /> Official source
          </a>
          {s.application_url && s.application_url !== s.official_url && (
            <a href={s.application_url} target="_blank" rel="noopener noreferrer" data-testid="application-link" className="inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:underline">
              <ExternalLink className="h-4 w-4" /> Application info
            </a>
          )}
          <div className="ml-auto flex items-center gap-2">
            {savedRow ? (
              <>
                <Select value={savedRow.status} onValueChange={(v) => updateSaved(savedRow.id, { status: v })}>
                  <SelectTrigger className="rounded-full h-9 w-[150px]" data-testid={`saved-status-${savedRow.id}`}><SelectValue /></SelectTrigger>
                  <SelectContent className="rounded-2xl">{SAVED_STATUSES.map((st) => <SelectItem key={st} value={st}>{st}</SelectItem>)}</SelectContent>
                </Select>
                <button onClick={() => removeSaved(savedRow.id)} data-testid={`saved-delete-${savedRow.id}`} className="p-2 rounded-full text-muted-foreground hover:text-destructive transition-colors"><Trash2 className="h-4 w-4" /></button>
              </>
            ) : (
              <Button onClick={() => saveGrant(opp)} disabled={savedIds.has(`${opp.source}:${opp.source_id}`)} data-testid={`save-grant-${opp.source_id}`} variant="outline" className="rounded-full gap-1.5 h-9">
                <Bookmark className="h-4 w-4" /> {savedIds.has(`${opp.source}:${opp.source_id}`) ? "Saved" : "Save"}
              </Button>
            )}
          </div>
        </div>

        {savedRow && (
          <div className="mt-4">
            <Label className="text-xs text-muted-foreground">Your notes</Label>
            <Textarea defaultValue={savedRow.notes} onBlur={(e) => { if (e.target.value !== savedRow.notes) updateSaved(savedRow.id, { notes: e.target.value }); }}
              placeholder="Private notes about this grant…" className="rounded-xl mt-1 min-h-[60px]" data-testid={`saved-notes-${savedRow.id}`} />
          </div>
        )}
      </Card>
    );
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6" data-testid="grant-finder-page">
      <div className="flex items-center gap-3">
        <div className="h-11 w-11 rounded-2xl bg-primary/10 flex items-center justify-center"><Landmark className="h-6 w-6 text-primary" /></div>
        <div>
          <h1 className="font-heading text-3xl sm:text-4xl">Grant Finder</h1>
          <p className="text-muted-foreground text-sm mt-0.5">Verified federal grant opportunities matched to your business — no guesswork, no invented data.</p>
        </div>
      </div>

      <Tabs defaultValue="find" className="w-full">
        <TabsList className="rounded-full">
          <TabsTrigger value="find" data-testid="tab-find" className="rounded-full">Find grants</TabsTrigger>
          <TabsTrigger value="profile" data-testid="tab-profile" className="rounded-full">Funding profile</TabsTrigger>
          <TabsTrigger value="saved" data-testid="tab-saved" className="rounded-full">Saved ({saved.length})</TabsTrigger>
        </TabsList>

        {/* FIND */}
        <TabsContent value="find" className="mt-6 space-y-5">
          <Card className="rounded-3xl border-border shadow-soft p-6">
            <div className="flex items-center gap-2 text-sm text-muted-foreground mb-3">
              <ShieldCheck className="h-4 w-4 text-primary" />
              Live source: <span className="text-foreground font-medium">Grants.gov (U.S. federal — nationwide)</span>
            </div>
            <p className="text-sm text-foreground/70">We search currently open federal opportunities and match them against your funding profile. Amounts, deadlines, eligibility and links come straight from the official source. Complete your funding profile for better matches.</p>
            <Button onClick={() => runSearch(false)} disabled={searching} data-testid="search-grants-button" className="rounded-full mt-4 gap-2 bg-primary hover:bg-primary/90">
              {searching ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />} Find matching grants
            </Button>
            {searchMeta && !searchMeta.complete && (
              <p className="text-xs text-amber-700 mt-3">Tip: add your industry and applicant type in the Funding profile tab to improve match quality.</p>
            )}
          </Card>

          {searching && <div className="text-center py-10 text-muted-foreground"><Loader2 className="h-6 w-6 animate-spin mx-auto mb-2" /> Searching Grants.gov…</div>}
          {results && !searching && (
            <>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="text-sm text-muted-foreground" data-testid="results-count">{results.length} {searchMeta?.all ? "opportunit" : "matching opportunit"}{results.length === 1 ? "y" : "ies"} {searchMeta?.all ? "shown" : "found"}.</p>
                {searchMeta?.ineligible > 0 && (
                  searchMeta.all ? (
                    <Button variant="ghost" size="sm" onClick={() => runSearch(false)} data-testid="hide-ineligible-button" className="rounded-full h-8 text-muted-foreground">
                      Hide ineligible
                    </Button>
                  ) : (
                    <Button variant="outline" size="sm" onClick={() => runSearch(true)} data-testid="show-ineligible-button" className="rounded-full h-8 gap-1.5">
                      <XCircle className="h-3.5 w-3.5" /> Browse all — including {searchMeta.ineligible} you're likely not eligible for
                    </Button>
                  )
                )}
              </div>
              <div className="space-y-4">{results.map((opp) => <OppCard key={opp.source_id} opp={opp} />)}</div>
            </>
          )}
        </TabsContent>

        {/* PROFILE */}
        <TabsContent value="profile" className="mt-6">
          <Card className="rounded-3xl border-border shadow-soft p-6 space-y-5" data-testid="funding-profile-form">
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Location (city, state)"><Input value={profile.location} onChange={(e) => setProfile({ ...profile, location: e.target.value })} placeholder="e.g., Richmond, VA" data-testid="profile-location" className="rounded-xl" /></Field>
              <Field label="State (2-letter, optional)"><Input value={profile.state} onChange={(e) => setProfile({ ...profile, state: e.target.value })} placeholder="e.g., VA" data-testid="profile-state" className="rounded-xl" /></Field>
              <Field label="Industry"><Input value={profile.industry} onChange={(e) => setProfile({ ...profile, industry: e.target.value })} placeholder="e.g., Health & wellness coaching" data-testid="profile-industry" className="rounded-xl" /></Field>
              <Field label="Applicant type">
                <Select value={profile.applicant_type} onValueChange={(v) => setProfile({ ...profile, applicant_type: v })}>
                  <SelectTrigger className="rounded-xl" data-testid="profile-applicant-type"><SelectValue placeholder="Select…" /></SelectTrigger>
                  <SelectContent className="rounded-2xl">{APPLICANT_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field label="Years in business"><Input value={profile.years_in_business} onChange={(e) => setProfile({ ...profile, years_in_business: e.target.value })} placeholder="e.g., 3" data-testid="profile-years" className="rounded-xl" /></Field>
              <Field label="Annual revenue"><Input value={profile.annual_revenue} onChange={(e) => setProfile({ ...profile, annual_revenue: e.target.value })} placeholder="e.g., $120,000" data-testid="profile-revenue" className="rounded-xl" /></Field>
              <Field label="Number of employees"><Input value={profile.num_employees} onChange={(e) => setProfile({ ...profile, num_employees: e.target.value })} placeholder="e.g., 2" data-testid="profile-employees" className="rounded-xl" /></Field>
            </div>
            <Field label="Funding needs"><Textarea value={profile.funding_needs} onChange={(e) => setProfile({ ...profile, funding_needs: e.target.value })} placeholder="What would the funding be used for? e.g., equipment, hiring, marketing, research…" data-testid="profile-funding-needs" className="rounded-xl min-h-[80px]" /></Field>
            <div>
              <Label className="text-sm">Owner demographics <span className="text-muted-foreground font-normal">(optional — used only as possible-match signals, never a guarantee)</span></Label>
              <div className="flex flex-wrap gap-2 mt-2" data-testid="profile-demographics">
                {DEMOGRAPHICS.map((d) => {
                  const on = profile.owner_demographics.includes(d);
                  return (
                    <button key={d} type="button" onClick={() => toggleDemo(d)} data-testid={`demo-${d.replace(/\s+/g, "-").toLowerCase()}`}
                      className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${on ? "bg-primary text-primary-foreground border-primary" : "bg-background text-muted-foreground border-border hover:bg-muted"}`}>
                      {d}
                    </button>
                  );
                })}
              </div>
            </div>
            <Button onClick={saveProfile} disabled={savingProfile} data-testid="save-profile-button" className="rounded-full gap-2 bg-primary hover:bg-primary/90">
              {savingProfile ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Save funding profile
            </Button>
          </Card>
        </TabsContent>

        {/* SAVED */}
        <TabsContent value="saved" className="mt-6 space-y-4">
          {saved.length === 0 ? (
            <Card className="rounded-3xl border-border shadow-soft p-10 text-center text-muted-foreground" data-testid="saved-empty">
              You haven't saved any grants yet. Save opportunities from the Find grants tab to track them here.
            </Card>
          ) : (
            <>
              <div className="flex items-center justify-between">
                <p className="text-sm text-muted-foreground">{saved.length} saved</p>
                <Button variant="outline" size="sm" onClick={() => setSavedSort((s) => (s === "deadline" ? "saved" : "deadline"))} data-testid="sort-saved-button" className="rounded-full gap-1.5 h-8">
                  <ArrowDownWideNarrow className="h-3.5 w-3.5" /> Sort: {savedSort === "deadline" ? "Deadline" : "Recently saved"}
                </Button>
              </div>
              <div className="space-y-4">{sortedSaved.map((row) => <OppCard key={row.id} savedRow={row} />)}</div>
            </>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
